import { useState } from "react";

import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";

const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT;
const SUCCESS_MESSAGE = "Thank you. Your quote request has been received.";

const initialFormState = {
  name: "",
  email: "",
  phone_number: "",
  project_type: "",
  project_description: "",
  website: "",
};

const projectTypes = [
  "Residential",
  "Commercial",
  "Renovation",
  "Consulting",
  "Plot Development",
  "Industrial Shed",
];

export default function QuoteForm() {
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      if (!FORMSPREE_ENDPOINT) {
        throw new Error("Quote form service is not configured.");
      }

      if (formData.website.trim()) {
        setSuccessMessage(SUCCESS_MESSAGE);
        setFormData(initialFormState);
        return;
      }

      const submissionData = new FormData();
      submissionData.append("name", formData.name.trim());
      submissionData.append("email", formData.email.trim());
      submissionData.append("phone_number", formData.phone_number.trim());
      submissionData.append("project_type", formData.project_type);
      submissionData.append(
        "project_description",
        formData.project_description.trim(),
      );
      submissionData.append("_gotcha", formData.website);
      submissionData.append("_subject", "New NM BuildTech Quote Request");

      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: submissionData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const providerMessage = errorData?.errors?.[0]?.message;
        throw new Error(
          providerMessage ||
            "Something went wrong while sending your request. Please try again.",
        );
      }

      setSuccessMessage(SUCCESS_MESSAGE);
      setFormData(initialFormState);
    } catch (error) {
      const message =
        error.message ||
        "Something went wrong while sending your request. Please try again.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (successMessage) {
    return (
      <div
        className="quote-form-shell relative overflow-hidden border border-[color:var(--border-architect)] bg-white px-6 py-12 md:px-10"
        data-testid="quote-form-success-shell"
      >
        <div className="relative z-10 mx-auto flex max-w-md flex-col items-center text-center">
          <div
            className="mb-8"
            data-testid="quote-form-success-logo"
          >
            <BrandLogo iconClassName="h-24 w-[170px] md:h-28 md:w-[200px]" layout="full" />
          </div>
          <p
            className="eyebrow-label mb-4"
            data-testid="quote-form-success-label"
          >
            Request received
          </p>
          <h3
            className="font-heading text-3xl font-semibold tracking-tight text-[#121212]"
            data-testid="quote-form-success-title"
          >
            Thank you.
          </h3>
          <p
            className="mt-4 max-w-sm text-sm leading-7 text-[#5c5c5c] md:text-base"
            data-testid="quote-form-success-message"
          >
            {successMessage}
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      action={FORMSPREE_ENDPOINT}
      className="quote-form-shell relative overflow-hidden border border-[color:var(--border-architect)] bg-white px-6 py-8 md:px-10 md:py-10"
      data-testid="quote-request-form"
      method="POST"
      onSubmit={handleSubmit}
    >
      <div className="relative z-10 space-y-7">
        <div className="flex items-center gap-4 border-b border-[color:var(--border-architect)] pb-5">
          <div
            data-testid="quote-form-logo"
          >
            <BrandLogo iconClassName="h-14 w-[110px] md:h-16 md:w-[128px]" layout="full" />
          </div>
          <div>
            <p
              className="text-sm text-[#5c5c5c]"
              data-testid="quote-form-brand-description"
            >
              Project intake specification sheet.
            </p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label data-testid="quote-name-label" htmlFor="name">
              Name
            </Label>
            <Input
              className="form-input h-12 rounded-none border-x-0 border-t-0 px-0 text-base shadow-none"
              data-testid="quote-name-input"
              id="name"
              name="name"
              onChange={handleChange}
              placeholder="Your full name"
              required
              value={formData.name}
            />
          </div>

          <div className="space-y-2">
            <Label data-testid="quote-email-label" htmlFor="email">
              Email Address
            </Label>
            <Input
              autoComplete="email"
              className="form-input h-12 rounded-none border-x-0 border-t-0 px-0 text-base shadow-none"
              data-testid="quote-email-input"
              id="email"
              name="email"
              onChange={handleChange}
              placeholder="name@example.com"
              required
              type="email"
              value={formData.email}
            />
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label data-testid="quote-phone-label" htmlFor="phone_number">
              Phone Number
            </Label>
            <Input
              autoComplete="tel"
              className="form-input h-12 rounded-none border-x-0 border-t-0 px-0 text-base shadow-none"
              data-testid="quote-phone-input"
              id="phone_number"
              name="phone_number"
              onChange={handleChange}
              placeholder="Your contact number"
              required
              value={formData.phone_number}
            />
          </div>

          <div className="space-y-2">
            <Label data-testid="quote-project-type-label" htmlFor="project_type">
              Project Type
            </Label>
            <Select
              onValueChange={(value) =>
                setFormData((current) => ({ ...current, project_type: value }))
              }
              value={formData.project_type}
            >
              <SelectTrigger
                className="form-input h-12 rounded-none border-x-0 border-t-0 px-0 text-base shadow-none"
                data-testid="quote-project-type-select"
                id="project_type"
              >
                <SelectValue placeholder="Select project type" />
              </SelectTrigger>
              <SelectContent>
                {projectTypes.map((projectType) => (
                  <SelectItem
                    data-testid={`quote-project-type-${projectType
                      .toLowerCase()
                      .replace(/\s+/g, "-")}-option`}
                    key={projectType}
                    value={projectType}
                  >
                    {projectType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label
            data-testid="quote-project-description-label"
            htmlFor="project_description"
          >
            Brief Project Description
          </Label>
          <Textarea
            className="form-input min-h-[120px] rounded-none border-x-0 border-t-0 px-0 text-base shadow-none"
            data-testid="quote-project-description-input"
            id="project_description"
            name="project_description"
            onChange={handleChange}
            placeholder="Tell us about your site, scope, or construction objective."
            value={formData.project_description}
          />
        </div>

        <div className="hidden" aria-hidden="true">
          <Label data-testid="quote-honeypot-label" htmlFor="website">
            Leave this field empty
          </Label>
          <input
            autoComplete="off"
            className="hidden"
            data-testid="quote-honeypot-input"
            id="website"
            name="website"
            onChange={handleChange}
            tabIndex="-1"
            type="text"
            value={formData.website}
          />
        </div>

        <Button
          className="group h-auto w-full rounded-none border border-[#121212] bg-[#121212] px-8 py-4 font-mono text-xs uppercase tracking-[0.28em] text-white transition-transform duration-300 hover:-translate-y-1 hover:bg-[#c0a062] hover:text-[#121212]"
          data-testid="quote-submit-button"
          disabled={isSubmitting || !formData.project_type}
          type="submit"
        >
          {isSubmitting ? "Submitting..." : "Request Quote"}
        </Button>
      </div>
    </form>
  );
}